import type React from "react";
import { Chip, Group, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import {
  STATUS_FILTERS,
  type ReviewStatusFilter,
} from "@utils/jobResultsHelpers";

interface ResultsFilterChipsProps {
  value: ReviewStatusFilter;
  onChange: (value: ReviewStatusFilter) => void;
  counts: Record<ReviewStatusFilter, number>;
  priorityOnly: boolean;
  onPriorityOnlyChange: (value: boolean) => void;
  priorityCount: number;
  missingOnly: boolean;
  onMissingOnlyChange: (value: boolean) => void;
  /** Rows the machine could not match (a TM value or a WD item). */
  missingCount: number;
}

const ResultsFilterChips: React.FC<ResultsFilterChipsProps> = ({
  value,
  onChange,
  counts,
  priorityOnly,
  onPriorityOnlyChange,
  priorityCount,
  missingOnly,
  onMissingOnlyChange,
  missingCount,
}) => {
  const { t } = useTranslation();
  return (
    <>
      <Group gap="xs" mb={4} wrap="wrap">
        <Chip.Group
          value={priorityOnly || missingOnly ? "" : value}
          onChange={(next) => onChange(next as ReviewStatusFilter)}
        >
          <Group gap="xs" wrap="wrap">
            {STATUS_FILTERS.map((filter) => (
              <Chip key={filter} value={filter} size="xs">
                {t(`jobResultsPage.filters.${filter}`, {
                  defaultValue: filter,
                })}
                {" "}
                ({counts[filter]})
              </Chip>
            ))}
          </Group>
        </Chip.Group>
        <Chip
          size="xs"
          color="orange"
          variant="filled"
          checked={priorityOnly}
          onChange={onPriorityOnlyChange}
          disabled={priorityCount === 0}
        >
          {t("jobResultsPage.filters.priorityOnly", {
            defaultValue: "Priority review",
          })}
          {" "}
          ({priorityCount})
        </Chip>
        {missingCount > 0 && (
          <Chip
            size="xs"
            color="red"
            variant="filled"
            checked={missingOnly}
            onChange={onMissingOnlyChange}
          >
            {t("jobResultsPage.filters.missingOnly", {
              defaultValue: "Not in catalogue",
            })}
            {" "}
            ({missingCount})
          </Chip>
        )}
      </Group>
      <Text size="xs" c="dimmed" mb="sm">
        {t("jobResultsPage.shortcuts.hint")}
      </Text>
    </>
  );
};

export default ResultsFilterChips;
